// src/components/compliance/ExpiringTab.tsx
//
// Grouped view of documents by proximity to expiry. Buckets:
//   Expired · ≤7d · ≤14d · ≤30d · ≤60d · ≤90d
//
// We fetch the two live sources — documents already in `expired` state
// AND documents in `active|approved|expiring` with `expiresAt` inside
// 90 days — then bucket client-side.

import { useMemo } from 'react';
import { useGetComplianceDocumentsQuery } from '../../api/complianceApi';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorState from '../common/ErrorState';
import { DocumentStatusChip, EnforcementModeChip } from './Chips';
import {
  daysUntil,
  formatShortDate,
  messageFromComplianceError,
  SUBJECT_TYPE_LABEL,
} from './complianceDisplay';
import type { ComplianceDocument } from '../../types/api';

interface ExpiringTabProps {
  workspaceId: string;
}

type BucketKey = 'expired' | '7' | '14' | '30' | '60' | '90';

const BUCKET_LABEL: Record<BucketKey, string> = {
  expired: 'Expired',
  '7': '≤ 7 days',
  '14': '≤ 14 days',
  '30': '≤ 30 days',
  '60': '≤ 60 days',
  '90': '≤ 90 days',
};

const BUCKET_ORDER: BucketKey[] = ['expired', '7', '14', '30', '60', '90'];

function bucketFor(doc: ComplianceDocument, now: Date): BucketKey | null {
  if (doc.status === 'expired') return 'expired';
  const d = daysUntil(doc.expiresAt, now);
  if (d === null) return null;
  if (d <= 0) return 'expired';
  if (d <= 7) return '7';
  if (d <= 14) return '14';
  if (d <= 30) return '30';
  if (d <= 60) return '60';
  if (d <= 90) return '90';
  return null;
}

export default function ExpiringTab({ workspaceId: _workspaceId }: ExpiringTabProps) {
  const {
    data: liveData,
    isLoading: liveLoading,
    error: liveError,
    refetch: refetchLive,
  } = useGetComplianceDocumentsQuery({
    status: ['active', 'approved', 'expiring'],
    expiringWithinDays: 90,
    limit: 500,
  });
  const {
    data: expiredData,
    isLoading: expiredLoading,
    error: expiredError,
  } = useGetComplianceDocumentsQuery({
    status: 'expired',
    limit: 500,
  });

  const now = useMemo(() => new Date(), []);

  const buckets = useMemo(() => {
    const map = new Map<BucketKey, ComplianceDocument[]>();
    for (const k of BUCKET_ORDER) map.set(k, []);
    for (const doc of [...(liveData?.rows ?? []), ...(expiredData?.rows ?? [])]) {
      const b = bucketFor(doc, now);
      if (!b) continue;
      map.get(b)!.push(doc);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        const da = daysUntil(a.expiresAt, now) ?? 0;
        const db = daysUntil(b.expiresAt, now) ?? 0;
        return da - db;
      });
    }
    return map;
  }, [liveData, expiredData, now]);

  const totalCount = Array.from(buckets.values()).reduce((n, arr) => n + arr.length, 0);

  if (liveLoading || expiredLoading) return <LoadingSpinner />;
  if (liveError) return <ErrorState title="Could not load expiring documents" message={messageFromComplianceError(liveError)} onRetry={refetchLive} />;
  if (expiredError) return <ErrorState title="Could not load expired documents" message={messageFromComplianceError(expiredError)} onRetry={refetchLive} />;

  return (
    <section className="space-y-6" data-testid="compliance-expiring-tab">
      {totalCount === 0 ? (
        <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-500">
          Nothing is expiring in the next 90 days, and nothing has expired.
        </div>
      ) : (
        BUCKET_ORDER.map((key) => {
          const docs = buckets.get(key)!;
          if (docs.length === 0) return null;
          const emphasize = key === 'expired' || key === '7';
          return (
            <section
              key={key}
              className={`rounded-lg border ${emphasize ? 'border-red-300' : 'border-gray-200'} bg-white`}
              data-testid={`compliance-expiring-bucket-${key}`}
            >
              <header className={`px-4 py-2 flex items-center justify-between border-b ${emphasize ? 'border-red-100 bg-red-50/40' : 'border-gray-100 bg-gray-50/60'}`}>
                <h3 className={`text-sm font-semibold ${emphasize ? 'text-red-800' : 'text-gray-800'}`}>{BUCKET_LABEL[key]}</h3>
                <span className="text-xs text-gray-600">{docs.length} document{docs.length === 1 ? '' : 's'}</span>
              </header>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="px-4 py-2">Requirement</th>
                      <th className="px-4 py-2">Subject</th>
                      <th className="px-4 py-2">Owner</th>
                      <th className="px-4 py-2">Status</th>
                      <th className="px-4 py-2">Enforcement</th>
                      <th className="px-4 py-2">Expires</th>
                      <th className="px-4 py-2 text-right">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {docs.map((doc) => {
                      const d = daysUntil(doc.expiresAt, now);
                      return (
                        <tr key={doc.id} className="hover:bg-gray-50" data-testid={`compliance-expiring-row-${doc.id}`}>
                          <td className="px-4 py-2 text-gray-900">
                            <div>{doc.requirement?.name ?? '—'}</div>
                            <div className="text-xs text-gray-500">{doc.title}</div>
                          </td>
                          <td className="px-4 py-2 text-gray-700 text-xs">
                            <div>{SUBJECT_TYPE_LABEL[doc.subjectType]}</div>
                            <div className="font-mono text-[10px] text-gray-500">{doc.subjectId}</div>
                          </td>
                          <td className="px-4 py-2 text-gray-700 text-xs">
                            {doc.owner?.fullName ?? doc.owner?.email ?? '—'}
                          </td>
                          <td className="px-4 py-2"><DocumentStatusChip status={doc.status} /></td>
                          <td className="px-4 py-2">
                            {doc.requirement && <EnforcementModeChip mode={doc.requirement.enforcementMode} />}
                          </td>
                          <td className="px-4 py-2 text-gray-700 text-xs">{formatShortDate(doc.expiresAt)}</td>
                          <td className="px-4 py-2 text-right text-xs">
                            {d === null ? '—' : d <= 0 ? <span className="text-red-700 font-semibold">{d} d</span> : <span>{d} d</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          );
        })
      )}
    </section>
  );
}
